import { useState } from 'react';
import { BillingPanel, Checkout, Mark, SignIn, ThemeToggle } from '@drbell/ui';
import { api } from '../api.ts';

/**
 * /plans — buying, keeping and cancelling a plan on the website (docs/32).
 *
 * A route area of the care site, like /partners: the same sign-in as the
 * rest of DrBell, so a plan bought here is the plan on every phone the
 * person signs in to. Prices are shown before sign-in; choosing one asks for
 * it. The page is loaded whole (not reached by an in-app link), because its
 * content policy is the one that lets Razorpay's checkout open
 * (apps/care/worker/headers.ts).
 */

export const isPlansPath = (pathname: string) => pathname === '/plans' || pathname.startsWith('/plans/');

export function BillingScreen({ signedIn, onSignedIn }: { signedIn: boolean; onSignedIn: () => void }) {
  const [asking, setAsking] = useState(false);
  const [refresh, setRefresh] = useState(0);
  /** One plan per person: with a plan, the chooser is not shown at all. */
  const [hasPlan, setHasPlan] = useState(false);

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-8">
      <header className="flex flex-wrap items-center gap-3 border-b border-(--line) pb-4">
        <Mark className="h-9 w-9 shrink-0" />
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">DrBell plans</h1>
          <p className="text-sm text-(--muted)">
            Unlimited medicines and reminders are free. A plan is for reaching the people who look after you.
          </p>
        </div>
        <ThemeToggle className="ml-auto" />
        <a href="/" className="rounded-lg border border-(--line) px-3 py-1.5 text-sm">Back</a>
      </header>

      {signedIn && <BillingPanel transport={api} refreshKey={refresh} onPlan={setHasPlan} />}

      {hasPlan ? null : asking && !signedIn ? (
        <SignIn
          title="Sign in to choose a plan"
          subtitle="The plan goes on your DrBell account, so it works on every phone you sign in to."
          onSignedIn={() => {
            setAsking(false);
            onSignedIn();
          }}
        />
      ) : (
        <Checkout
          transport={api}
          signedIn={signedIn}
          onSignIn={() => setAsking(true)}
          onActive={() => setRefresh((n) => n + 1)}
        />
      )}
    </div>
  );
}
